import {
  CVBuilder,
  CVBuilderV2,
  DEFAULT_CV_LOCALE,
  I18nProvider,
  PlatformProvider,
  useCV,
  useI18n,
} from '@cv-builder/core'
import { useCallback, useEffect, useState } from 'react'
import '@cv-builder/core/styles/tailwind.css'
import {
  createDanishSampleCV,
  createEmptyCV,
  createLocalAdapter,
  createSampleCV,
} from '@cv-builder/adapter-local'

// Create the adapter once.
// PDF generation goes through a same-origin '/pdf' path (nginx reverse-proxies it
// to the internal pdf-service in staging; vite dev-proxies it to localhost:3100).
// This avoids hardcoding an unreachable 'http://localhost:3100' into the deployed
// bundle and keeps the pdf-service off the public internet.
const adapter = createLocalAdapter({ pdfServiceUrl: '/pdf' })

// Check if we're in development mode
const isDev = import.meta.env.DEV

// Render the experimental v2 shell when the URL has ?v2 (e.g. localhost:3000/?v2)
const useV2Shell =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('v2')

// Dev toggle component - only visible in development
function DevToggle({
  onSampleChange,
  sampleMode,
  isThemePreview,
  onThemeToggle,
}: {
  onSampleChange: (sampleMode: SampleMode) => void
  sampleMode: SampleMode
  isThemePreview: boolean
  onThemeToggle: (enabled: boolean) => void
}) {
  const { t } = useI18n()

  if (!isDev) return null

  return (
    <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg text-sm font-mono">
      <span className="text-gray-400">{t('app.devMode')}:</span>
      <label className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors">
        <input
          type="checkbox"
          checked={isThemePreview}
          onChange={(event) => onThemeToggle(event.target.checked)}
          className="sr-only"
        />
        <span
          className={`h-3 w-6 rounded-full p-0.5 transition-colors ${
            isThemePreview ? 'bg-emerald-500' : 'bg-gray-500'
          }`}
          aria-hidden="true"
        >
          <span
            className={`block h-2 w-2 rounded-full bg-white transition-transform ${
              isThemePreview ? 'translate-x-3' : ''
            }`}
          />
        </span>
        <span>{t('builder.devTheme')}</span>
      </label>
      <button
        type="button"
        onClick={() => onSampleChange('english')}
        className={`px-2 py-1 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 ${
          sampleMode === 'english'
            ? 'bg-blue-600 text-white'
            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
        }`}
      >
        {t('app.sampleData')}
      </button>
      <button
        type="button"
        onClick={() => onSampleChange('danish')}
        className={`px-2 py-1 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 ${
          sampleMode === 'danish'
            ? 'bg-blue-600 text-white'
            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
        }`}
      >
        {t('app.danishSampleData')}
      </button>
      <button
        type="button"
        onClick={() => onSampleChange('empty')}
        className={`px-2 py-1 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 ${
          sampleMode === 'empty'
            ? 'bg-blue-600 text-white'
            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
        }`}
      >
        {t('app.cleanSlate')}
      </button>
    </div>
  )
}

type SampleMode = 'english' | 'danish' | 'empty'

function CVBuilderApp() {
  const { cv, isLoading, updateCV, createCV, loadCV } = useCV()
  const { t } = useI18n()
  const defaultCvLocale = adapter.config.locale?.defaultCvLocale ?? DEFAULT_CV_LOCALE
  const [_cvId, setCvId] = useState<string | null>(null)
  const [sampleMode, setSampleMode] = useState<SampleMode>('empty')
  const [isThemePreview, setIsThemePreview] = useState(false)

  useEffect(() => {
    // Try to load existing CV from localStorage or create new one
    const initCV = async () => {
      try {
        const cvs = await adapter.storage.listCVs()
        if (cvs.length > 0 && cvs[0]) {
          setCvId(cvs[0].id)
          await loadCV(cvs[0].id)
        } else {
          const newCV = await createCV({ title: t('app.defaultCvTitle'), locale: defaultCvLocale })
          setCvId(newCV.id)
        }
      } catch (error) {
        console.error('Failed to initialize CV:', error)
        const newCV = await createCV({ title: t('app.defaultCvTitle'), locale: defaultCvLocale })
        setCvId(newCV.id)
      }
    }
    initCV()
  }, [createCV, defaultCvLocale, loadCV, t])

  const handleSampleChange = useCallback(
    (nextSampleMode: SampleMode) => {
      setSampleMode(nextSampleMode)
      const nextCV =
        nextSampleMode === 'english'
          ? createSampleCV()
          : nextSampleMode === 'danish'
            ? createDanishSampleCV()
            : createEmptyCV()

      void updateCV({
        title: nextCV.title,
        templateId: nextCV.templateId,
        locale: nextCV.locale,
        personalInfo: nextCV.personalInfo,
        experiences: nextCV.experiences,
        education: nextCV.education,
        skills: nextCV.skills,
        languages: nextCV.languages,
        certifications: nextCV.certifications,
        customSections: nextCV.customSections,
      })
    },
    [updateCV]
  )

  if (isLoading || !cv) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <p className="text-gray-500">{t('app.loadingCvBuilder')}</p>
      </div>
    )
  }

  return (
    <>
      {useV2Shell ? (
        <CVBuilderV2 cv={cv} onChange={updateCV} />
      ) : (
        <CVBuilder cv={cv} onChange={updateCV} devTheme={isThemePreview ? 'hive' : null} />
      )}
      <DevToggle
        onSampleChange={handleSampleChange}
        sampleMode={sampleMode}
        isThemePreview={isThemePreview}
        onThemeToggle={setIsThemePreview}
      />
    </>
  )
}

export function App() {
  const localeConfig = adapter.config.locale

  return (
    <I18nProvider
      locale={localeConfig?.defaultUiLocale}
      supportedLocales={localeConfig?.supportedLocales}
    >
      <PlatformProvider adapter={adapter}>
        <CVBuilderApp />
      </PlatformProvider>
    </I18nProvider>
  )
}
